import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Box,
  Card,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import {
  getAllDemoRequests,
  rejectDemoRequest,
  stopDemoRequest, // ✅ إضافة stopDemoRequest
} from "../../../../Services/DemoApi"; // ✅ استيراد stopDemoRequest
import AcceptDemo from "./AcceptDemo"; // Import AcceptDemo component
import { getPlane } from "../../../../Services/plansApi"; // Import getPlane function
import ActivateDemoToPaid from "./ActivateDemoToPaid"; // import the new component
import { getSupportLogs } from "../../../../Services/ReportsApi"; // Import getSupportLogs

const DemoVersionTap = () => {
  const [demoRequests, setDemoRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDemoId, setSelectedDemoId] = useState(null); // State for selected demo ID
  const [selectedDemoName, setSelectedDemoName] = useState(""); // State for selected demo name
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false); // State for reject confirmation dialog
  const [selectedConversionId, setSelectedConversionId] = useState(null); // ID of request being converted
  const [conversionDialogOpen, setConversionDialogOpen] = useState(false); // State for conversion dialog visibility
  const [conversionData, setConversionData] = useState({}); // State for conversion data
  const [dialogOpen, setDialogOpen] = useState(false); // State for dialog visibility
  const [supportLogs, setSupportLogs] = useState([]); // State for support logs
  const [stopDialogOpen, setStopDialogOpen] = useState(false); // State for stop confirmation dialog
  const [selectedStopId, setSelectedStopId] = useState(null); // ID of demo to stop
  const [selectedStopTenant, setSelectedStopTenant] = useState(""); // Tenant name to stop

  // new: search state
  const [searchQuery, setSearchQuery] = useState("");

  // new: broker filter state
  const [brokerFilter, setBrokerFilter] = useState("");

  // new: creation date range state
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // new: end date range state (added)
  const [endDateFrom, setEndDateFrom] = useState("");
  const [endDateTo, setEndDateTo] = useState("");

  // new: activator filter state
  const [activatorFilter, setActivatorFilter] = useState("");

  // new: status filter state
  const [statusFilter, setStatusFilter] = useState("");

  // Helper function to extract username from email
  const getEmailUsername = (email) => {
    return email ? email.split('@')[0] : '';
  };

  // Helper function to find activator for a tenant
  const findActivator = (tenantName) => {
    const log = supportLogs.find(
      (log) => log.tenant?.id === tenantName && log.type === "active_demo"
    );
    return log?.user || null;
  };

  // new: derive unique broker options from demoRequests
  const brokerOptions = React.useMemo(() => {
    const map = new Map();
    demoRequests.forEach((r) => {
      if (r.broker && r.broker.id) {
        map.set(r.broker.id, r.broker.name);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [demoRequests]);

  // new: derive unique activator options from supportLogs (type === 'active_demo')
  const activatorOptions = React.useMemo(() => {
    const map = new Map();
    supportLogs.forEach((log) => {
      if (log.type === "active_demo" && log.user && log.user.id) {
        map.set(log.user.id, log.user.name || log.user.email || `#${log.user.id}`);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [supportLogs]);

  // new: status options (static)
const statusOptions = React.useMemo(
  () => [
    { id: "", name: "كل الحالات" },
    { id: "pending", name: "قيد الانتظار" },
    { id: "approved", name: "مفعّلة" },
    { id: "finashed", name: "متوقفة" },
    { id: "rejected", name: "مرفوضة" },
    { id: "converted", name: "محولة" },
  ],
  []
);

  useEffect(() => {
    // Fetch demo requests
    getAllDemoRequests()
      .then((res) => {
        setDemoRequests(res.data.data || []);
      })
      .catch(() => setDemoRequests([]))
      .finally(() => setLoading(false));

    // Fetch support logs
    getSupportLogs()
      .then((res) => {
        setSupportLogs(res.data || []);
      })
      .catch((err) => {
        console.error("Error fetching support logs:", err);
        setSupportLogs([]);
      });
  }, []);

  const handleOpenDialog = (id) => {
    setSelectedDemoId(id); // Set the selected demo ID
    setDialogOpen(true); // Open the dialog
  };

  const handleCloseDialog = () => {
    setDialogOpen(false); // Close the dialog
    setSelectedDemoId(null); // Reset the selected demo ID
  };

  const handleOpenRejectDialog = (id, name) => {
    setSelectedDemoId(id);
    setSelectedDemoName(name);
    setRejectDialogOpen(true);
  };

  const handleCloseRejectDialog = () => {
    setRejectDialogOpen(false);
    setSelectedDemoId(null);
    setSelectedDemoName("");
  };

  const handleRejectDemo = async () => {
    try {
      await rejectDemoRequest(selectedDemoId); // ✅ استدعاء دالة رفض الطلب
      setDemoRequests((prev) =>
        prev.map((r) => (r.id === selectedDemoId ? { ...r, status: "rejected" } : r))
      );
      handleCloseRejectDialog();
    } catch (err) {
      console.error("Error rejecting demo request:", err);
    }
  };

  const handleOpenConversionDialog = async (id) => {
    const selectedRequest = demoRequests.find((request) => request.id === id);
    if (selectedRequest) {
      setSelectedConversionId(id);

      setConversionData({
        tenant_id: selectedRequest.user?.tenant_id || "", // ✅ نأخذ tenant_id من داخل user
        plane_id: selectedRequest.plane_id || selectedRequest.plane?.id || "",
        start_date: selectedRequest.start_date || "",
        end_date: selectedRequest.end_date || "",
      });

      setConversionDialogOpen(true);
    }
  };

  const handleCloseConversionDialog = () => {
    setConversionDialogOpen(false);
    setConversionData({});
  };

  const handleOpenStopDialog = (id, tenantName) => {
    setSelectedStopId(id);
    setSelectedStopTenant(tenantName);
    setStopDialogOpen(true);
  };

  const handleCloseStopDialog = () => {
    setStopDialogOpen(false);
    setSelectedStopId(null);
    setSelectedStopTenant("");
  };

  const handleStopDemo = async (id, tenantName) => {
    try {
      await stopDemoRequest(tenantName); // ✅ إرسال tenant_name فقط بدلاً من id
      setDemoRequests((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, status: "finashed" } : r
        )
      );
      handleCloseStopDialog();
      console.log(`تم إيقاف النسخة التجريبية للمستأجر: ${tenantName}`);
    } catch (err) {
      console.error("Error stopping demo request:", err);
    }
  };

  // new: handler for search input
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // new: handler for broker select
  const handleBrokerFilterChange = (e) => {
    setBrokerFilter(e.target.value);
  };

  // new: handler for activator select
  const handleActivatorFilterChange = (e) => {
    setActivatorFilter(e.target.value);
  };

  // new: handler for status select
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  // new: handlers for date inputs
  const handleDateFromChange = (e) => setDateFrom(e.target.value);
  const handleDateToChange = (e) => setDateTo(e.target.value);

  // new: handlers for end date inputs
  const handleEndDateFromChange = (e) => setEndDateFrom(e.target.value);
  const handleEndDateToChange = (e) => setEndDateTo(e.target.value);

  // updated: prepare filtered list based on searchQuery, brokerFilter, activatorFilter and date ranges
  const filteredRequests = demoRequests.filter((r) => {
    const q = searchQuery.trim().toLowerCase();

    // apply broker filter
    if (brokerFilter && String(r.broker?.id) !== String(brokerFilter)) return false;

    // apply status filter
    if (statusFilter && String(r.status) !== String(statusFilter)) return false;

    // apply activator filter
    const activator = findActivator(r.tenant_name);
    if (activatorFilter && String(activator?.id) !== String(activatorFilter)) return false;

    // apply creation date range filter on created_at (if provided)
    if (dateFrom || dateTo) {
      const created = r.created_at ? new Date(r.created_at) : null;
      if (!created) return false;
      if (dateFrom) {
        const from = new Date(dateFrom);
        from.setHours(0,0,0,0);
        if (created < from) return false;
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23,59,59,999);
        if (created > to) return false;
      }
    }

    // apply end date range filter on end_date (if provided)
    if (endDateFrom || endDateTo) {
      const ended = r.end_date ? new Date(r.end_date) : null;
      if (!ended) return false;
      if (endDateFrom) {
        const eFrom = new Date(endDateFrom);
        eFrom.setHours(0,0,0,0);
        if (ended < eFrom) return false;
      }
      if (endDateTo) {
        const eTo = new Date(endDateTo);
        eTo.setHours(23,59,59,999);
        if (ended > eTo) return false;
      }
    }

    if (!q) return true;

    const fields = [
      r.tenant_name,
      r.id && String(r.id),
      r.status,
      r.created_at,
      r.start_date,
      r.end_date,
      r.plane?.plane_name,
      r.user?.name,
      r.user?.email,
      r.user?.phone,
      r.broker?.name,
      r.broker?.email,
      r.broker?.phone,
    ];

    // also check activator name/email if support logs loaded
    // reuse activator variable declared earlier (avoid redeclaration)
    if (activator) {
      fields.push(activator.name, activator.email);
    }

    return fields
      .filter(Boolean)
      .some((f) => String(f).toLowerCase().includes(q));
  });

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
          النسخ التجريبية
        </Typography>
        <Chip 
          label={`${filteredRequests.length} نسخة`} 
          color="primary" 
          sx={{ fontSize: '1rem', px: 1 }}
        />
      </Box>

      {/* Search and Filters */}
      <Card sx={{ p: 3, mb: 3, boxShadow: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ mr: 1, color: '#1976d2' }} />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            البحث والفلاتر
          </Typography>
        </Box>

        {/* Row 1: Search */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="ابحث باسم المستأجر، العميل، الموزع، الحالة أو التاريخ..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
          />
        </Box>

        {/* Row 2: User Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
            <InputLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon fontSize="small" />
                الموزع
              </Box>
            </InputLabel>
            <Select
              value={brokerFilter}
              onChange={handleBrokerFilterChange}
              label="الموزع"
            >
              <MenuItem value="">كل الموزعين</MenuItem>
              {brokerOptions.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
            <InputLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon fontSize="small" />
                المفعّل
              </Box>
            </InputLabel>
            <Select
              value={activatorFilter}
              onChange={handleActivatorFilterChange}
              label="المفعّل"
            >
              <MenuItem value="">كل المفعّلين</MenuItem>
              {activatorOptions.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
            <InputLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AssignmentIcon fontSize="small" />
                الحالة
              </Box>
            </InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label="الحالة"
            >
              {statusOptions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Row 3: Date Filters */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 200 }}>
            <CalendarTodayIcon fontSize="small" color="action" />
            <TextField
              size="small"
              type="date"
              label="من تاريخ الإنشاء"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={handleDateFromChange}
              fullWidth
            />
          </Box>

          <TextField
            size="small"
            type="date"
            label="إلى تاريخ الإنشاء"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={handleDateToChange}
            sx={{ flex: 1, minWidth: 200 }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 200 }}>
            <CalendarTodayIcon fontSize="small" color="action" />
            <TextField
              size="small"
              type="date"
              label="من تاريخ النهاية"
              InputLabelProps={{ shrink: true }}
              value={endDateFrom}
              onChange={handleEndDateFromChange}
              fullWidth
            />
          </Box>

          <TextField
            size="small"
            type="date"
            label="إلى تاريخ النهاية"
            InputLabelProps={{ shrink: true }}
            value={endDateTo}
            onChange={handleEndDateToChange}
            sx={{ flex: 1, minWidth: 200 }}
          />
        </Box>
      </Card>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      ) : demoRequests.length > 0 ? (
        <TableContainer 
          component={Paper} 
          sx={{ 
            boxShadow: 3,
            '& .MuiTable-root': {
              minWidth: 1200,
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1976d2' }}>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>العميل</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>الموزع</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>اسم المستأجر</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>تم قبول النسخة من خلال</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>الحالة</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>اسم الخطة</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>تاريخ البداية</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>تاريخ النهاية</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>ملاحظات</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>تاريخ الإنشاء</TableCell>
                <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>إجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={11} sx={{ py: 8 }}>
                    <Typography variant="h6" color="text.secondary">
                      لا توجد نتائج مطابقة
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRequests.map((request, index) => {
                  const activator = findActivator(request.tenant_name);
                  return (
                    <TableRow 
                      key={request.id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                        '&:hover': {
                          backgroundColor: '#e3f2fd',
                          transition: 'background-color 0.3s',
                        },
                      }}
                    >
                      <TableCell align="right">
                        {request.user ? (
                          <div>
                            <div><strong>{request.user.name}</strong></div>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              {getEmailUsername(request.user.email)}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              {request.user.phone}
                            </div>
                          </div>
                        ) : (
                          "غير متوفر"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {request.broker ? (
                          <div>
                            <div><strong>{request.broker.name}</strong></div>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              {getEmailUsername(request.broker.email)}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              {request.broker.phone}
                            </div>
                          </div>
                        ) : (
                          "غير متوفر"
                        )}
                      </TableCell>
                      <TableCell align="right">{request.tenant_name}</TableCell>
                      <TableCell align="right">
                        {activator ? (
                          <div>
                            <div><strong>{activator.name}</strong></div>
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              {getEmailUsername(activator.email)}
                            </div>
                          </div>
                        ) : (
                          "غير متوفر"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {(() => {
                          const statusMap = {
                            rejected: { label: "مرفوضة", color: "error" },
                            approved: { label: "تعمل", color: "success" },
                            pending: { label: "قيد الانتظار", color: "warning" },
                            finashed: { label: "متوقفة", color: "default" },
                          };
                          const statusInfo =
                            statusMap[request.status] || {
                              label: "غير معروف",
                              color: "default",
                            };
                          return (
                            <Chip
                              label={statusInfo.label}
                              color={statusInfo.color}
                              size="small"
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        {request.plane?.plane_name || "غير محدد"}
                      </TableCell>
                      <TableCell align="right">{request.start_date || "غير متوفر"}</TableCell>
                      <TableCell align="right">{request.end_date || "غير متوفر"}</TableCell>
                      <TableCell align="right">{request.notes || "لا توجد ملاحظات"}</TableCell>
                      <TableCell align="right">{request.created_at || "غير متوفر"}</TableCell>
                      <TableCell align="right">
                        {request.status === "pending" && (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              variant="contained"
                              color="success"
                              onClick={() => handleOpenDialog(request.id)}
                              size="small"
                              sx={{ 
                                minWidth: 80,
                                boxShadow: 2,
                                '&:hover': { boxShadow: 4 },
                              }}
                            >
                              قبول
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                handleOpenRejectDialog(request.id, request.tenant_name)
                              }
                              size="small"
                              sx={{ minWidth: 80 }}
                            >
                              رفض
                            </Button>
                          </Box>
                        )}
                        {request.status === "approved" && (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={() => handleOpenConversionDialog(request.id)}
                              size="small"
                              sx={{ 
                                boxShadow: 2,
                                '&:hover': { boxShadow: 4 },
                              }}
                            >
                              تحويل
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => handleOpenStopDialog(request.id, request.tenant_name)}
                              size="small"
                            >
                              إيقاف
                            </Button>
                          </Box>
                        )}
                        {request.status === "finashed" && (
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handleOpenConversionDialog(request.id)}
                            size="small"
                            sx={{ 
                              boxShadow: 2,
                              '&:hover': { boxShadow: 4 },
                            }}
                          >
                            تحويل إلى نسخة كاملة
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Card sx={{ p: 8, textAlign: 'center', boxShadow: 3 }}>
          <Typography variant="h5" color="text.secondary">
            لا توجد نسخ تجريبية متوفرة
          </Typography>
        </Card>
      )}

      {/* Dialog for AcceptDemo */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>قبول النسخة التجريبية</DialogTitle>
        <DialogContent>
          {selectedDemoId && (
            <AcceptDemo
              demoId={selectedDemoId}
              tenantName={demoRequests.find((request) => request.id === selectedDemoId)?.tenant_name || ""}
              onSuccess={() => {
                handleCloseDialog();
                setDemoRequests((prev) =>
                  prev.map((request) =>
                    request.id === selectedDemoId
                      ? { ...request, status: "approved" }
                      : request
                  )
                );
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog for Reject Confirmation */}
      <Dialog open={rejectDialogOpen} onClose={handleCloseRejectDialog}>
        <DialogTitle>تأكيد الرفض</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من رفض الطلب الخاص بالمستأجر "{selectedDemoName}"؟
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog}>إلغاء</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectDemo}
          >
            تأكيد الرفض
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for Stop Demo Confirmation */}
      <Dialog open={stopDialogOpen} onClose={handleCloseStopDialog}>
        <DialogTitle>تأكيد إيقاف النسخة التجريبية</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            هل أنت متأكد من إيقاف النسخة التجريبية للمستأجر "{selectedStopTenant}"؟
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
            ⚠️ تحذير: في حالة الإيقاف، لا يمكن تشغيل النسخة التجريبية مرة أخرى إلا بعد التحويل إلى نسخة مفعلة (مدفوعة).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStopDialog}>إلغاء</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleStopDemo(selectedStopId, selectedStopTenant)}
          >
            تأكيد الإيقاف
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for conversion confirmation */}
      <Dialog
        open={conversionDialogOpen}
        onClose={handleCloseConversionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>تحويل إلى نسخة كاملة</DialogTitle>
        <DialogContent>
          <ActivateDemoToPaid
            tenant_id={conversionData.tenant_id}
            defaultData={conversionData}
            onClose={handleCloseConversionDialog}
            onSuccess={(result) => {
              handleCloseConversionDialog();
              setDemoRequests((prev) =>
                prev.map((r) =>
                  r.id === selectedConversionId
                    ? { ...r, status: "converted", ...result }
                    : r
                )
              );
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DemoVersionTap;
